import {Injectable}                     from '@angular/core';
import {ApShowPointsOnMapService}       from '../../ap-utils/service/ap-show-points-on-map.service';
import {TranslationStore}               from '../../stores/translation/translation.store';
import {ApDynformsConfigFieldset}       from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {map}                            from 'rxjs/operators';
import {ApDynformsConfigNumerictextbox} from '../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {NFertilizationStore}            from '../../stores/n-fertilization/n-fertilization.store';
import {ObjectFactory}                  from 'ts-tooling';
import {ApDynformsConfigComboBox}       from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {FertilizerStore}                from '../../stores/base-data/fertilizer.store';
import {DriverStore}                    from '../../stores/docu/driver.store';
import {MachineStore}                   from '../../stores/docu/machine.store';
import {InstrumentStore}                from '../../stores/docu/instrument.store';
import {LanguageStore}                  from '../../stores/translation/language.store';
import {MapStore}                       from '../../stores/map/map.store';
import {ApDateService}                  from '../../ap-core/services/ap-date-service';
import {GetApplicationModeService}      from '../../ap-utils/service/ap-get-applicationmode.service';
import {GetOperationModeService}        from '../../ap-utils/service/ap-get-operationmode.service';
import {CropApplicationStore}           from '../../stores/task-management/crop-application.store';
import {ApDynformsConfigTextbox}        from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {FieldStore}                     from '../../stores/farm/field.store';
import {CropTypeStore}                  from '../../stores/base-data/crop.types.store';
import {ApplicationModesEnum}           from '../../stores/common/appl-modes.store';
import {UnitsStore}                     from '../../stores/common/units.store';
import IGuid = System.IGuid;
import IDatasetFieldMap = Data.Sensor.IDatasetFieldMap;
import INFertilizationPlanBook = Data.TaskManagement.INFertilizationPlanBook;
import INFertilizationSummary = Data.TaskManagement.INFertilizationSummary;
import IApplicationMode = Data.Common.IApplicationMode;

@Injectable({
  providedIn: 'root'
})
export class ApNFertilizationLogfilesService {
  private static _getJson(log: IDatasetFieldMap): any {
    return JSON.parse(log.DataSet.HeaderValuesString);
  }

  constructor(private applicationModeService: GetApplicationModeService,
              private cropApplicationStore: CropApplicationStore,
              private cropTypeStore: CropTypeStore,
              private dateService: ApDateService,
              private driverStore: DriverStore,
              private fertilizationStore: FertilizerStore,
              private fieldStore: FieldStore,
              private instrumentStore: InstrumentStore,
              private languageStore: LanguageStore,
              private mapStore: MapStore,
              private machineStore: MachineStore,
              private unitStore: UnitsStore,
              private nFertilizationStore: NFertilizationStore,
              private translationStore: TranslationStore,
              private operationModeService: GetOperationModeService,
              private showPointsOnMapService: ApShowPointsOnMapService) {
  }

  public toggleLogPointsOnMap(selectedItems: any[], farmId: number): void {
    let selectedMin = Number.MAX_SAFE_INTEGER;
    let selectedMax = 0;
    let showLegend = true;

    if (selectedItems && selectedItems?.length > 0) {
      for (const d of selectedItems) {
        // check min and max of the other selected items and adjust min/max boundaries
        if (d?.SourceItem?.NUptakeMin < selectedMin) {
          selectedMin = d?.SourceItem?.NUptakeMin;
        }
        if (d?.SourceItem?.NUptakeMax > selectedMax) {
          selectedMax = d?.SourceItem?.NUptakeMax;
        }
      }
    }

    // if there is no or invalid data we hide the legend
    if (selectedMin === Number.MAX_SAFE_INTEGER || !isFinite(selectedMax) || !isFinite(selectedMin)) {
      showLegend = false;
    }

    // finally update legend values/titles/colors and reload map considering
    // the new min/max boundaries and the corresponding changed colors.
    // To make this work properly the MapFactoryStyler cache needs to be cleared
    // everytime the min/max is recalculated
    this.mapStore.Legends.LogLegend.updateValues(selectedMin, selectedMax);
    this.mapStore.Legends.refreshLegend(this.mapStore.Legends.LogLegend, showLegend);
    this.showPointsOnMapService.showPoints(selectedItems, farmId);
  }

  public onAccept(dataItem: any | any[]): void {
    if (!Array.isArray(dataItem)) {
      dataItem = [dataItem];
    }
    this.nFertilizationStore.BookPlanBooks(
      this._getLogfiles(dataItem).map((log) => ({planBook: log.NPlanBook, logfile: log}))
    );
  }

  public onReset(dataItem: any | any[]): void {
    if (!Array.isArray(dataItem)) {
      dataItem = [dataItem];
    }
    this.nFertilizationStore.UnBookPlanBooks(
      this._getLogfiles(dataItem).map((log) => ({planBook: log.NPlanBook, logfile: log}))
    );
  }

  public onReject(dataItem: any | any[]): void {
    if (!Array.isArray(dataItem)) {
      dataItem = [dataItem];
    }
    const logFiles = this._getLogfiles(dataItem).map((log) => {
      log = ObjectFactory.Copy(log);
      log.Status = -1;
      if (!!log?.NPlanBook?.BookedBy &&
        this._getNPlanBookLogfiles(log.NPlanBook?.Id).filter(l => l.Id !== log.Id).every(l => l.Status < 1)) {
        this.nFertilizationStore.UnBookPlanBooks([{planBook: log.NPlanBook, logfile: log}]);
        return null;
      }

      if (log.NPlanBook?.ProductId == null) {
        return null;
      }

      return log;
    }).filter(log => !!log);
    this.nFertilizationStore.UpdateLogfiles(logFiles);
  }

  public getEditFieldSets(): ApDynformsConfigFieldset[] {
    return [
      new ApDynformsConfigFieldset({
        key: 'Logfile',
        legend: 'N__Logfile',
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'WorkingWidth',
            label: 'Docu_Ini__WorkingWidth_m',
            min: 0,
            max: 100,
          }),
          new ApDynformsConfigTextbox({
            key: 'Note',
            label: 'Global__Note',
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'Planning',
        legend: 'Global__Planning',
        config: [
          new ApDynformsConfigComboBox({
            key: 'Planning.ProductId',
            label: 'Global__Product',
            valueField: 'Id',
            textField: 'Prodname',
            options: this.fertilizationStore.Fertilizer$.pipe(
              map((fertilizers) => [
                {
                  Id: 0,
                  ContentN: 100,
                  Prodname: this.translationStore.FindTranslationForSelectedLanguage('Nutrient__PureNutrient')
                },
                ...fertilizers
              ]),
            ),
          }),
          new ApDynformsConfigComboBox({
            key: 'Driver',
            label: 'Docu_Ini__Motorist',
            valueField: 'Id',
            textField: 'ShortFullName',
            options: this.driverStore.Listen(s => s.data)
          }),
          new ApDynformsConfigComboBox({
            key: 'Machine',
            label: 'AgriConnect__Engine',
            valueField: 'Id',
            textField: 'NameWithSign',
            options: this.machineStore.Listen(s => s.data)
          }),
          new ApDynformsConfigComboBox({
            key: 'Attachment',
            label: 'AgriConnect_Ini__Tool',
            valueField: 'Id',
            textField: 'Name',
            options: this.instrumentStore.Listen(s => s.data)
          })
        ]
      })
    ];
  }

  public generatePlanning(items: any[]): void {
    const logFiles = this._getLogfiles(items).filter((log) => !log.NPlanBook);
    const summaryLogs = this._splitLogs(logFiles);
    const updateLogs = [];

    summaryLogs.forEach((sLogs, i) => {
      const ec = sLogs.Reduce((cont, cur) => cont < cur.Ec ? cont : cur.Ec, undefined);
      let standard = sLogs.Reduce((_, log) => {
        const json = ApNFertilizationLogfilesService._getJson(log);
        return json['Standard'] ? +json['Standard'] : (log.NContentMin + log.NContentMax) / 2 * log.Area;
      }, 0) / sLogs.map(log => log.Area).Sum();
      const applicationRate = sLogs.Reduce((sum, log) => {
        const json = ApNFertilizationLogfilesService._getJson(log);
        return sum + (json['ReferenceRate'] ? +(json['ReferenceRate'].Split(' ')[0]) : standard) * log.Area;
      }, 0) / sLogs.map(log => log.Area).Sum();
      const dateString = this.dateService.toFarmDate(new Date()).format(this.languageStore.CurrentDateTimeFormat);
      const dbmCount = sLogs.filter(l => ApNFertilizationLogfilesService._getJson(l)['DeadBiomass'] !== undefined).length;
      const logFile = sLogs[0];
      const operationMode = this.operationModeService.GetOperationModeByLoggerId(+ApNFertilizationLogfilesService._getJson(logFile)['OperationMode']);
      const applicationMode = this._getApplicationModeId(logFile);
      const unit = this.unitStore.getUnit('kgN/ha');
      const summary = {
        Id: undefined,
        ECValue: ec,
        OperationMode: operationMode,
        ApplicationMode: applicationMode,
        ApplicationDate: sLogs.Reduce((min, l) => min < l.DataSet.Created ? min : l.DataSet.Created, undefined),
        AdditionalValue: sLogs.Reduce((current, log) => {
          const json = ApNFertilizationLogfilesService._getJson(log);
          if (json['DeadBiomass'] === undefined) {
            return current;
          }
          const dbm = dbmCount === 0 ? undefined : json['DeadBiomass'] / dbmCount;
          return {
            DeadBiomass: current.DeadBiomass ? current.DeadBiomass + dbm : dbm,
            ExpectedYield: undefined,
            NMinPotential: undefined
          };
        }, {DeadBiomass: undefined, ExpectedYield: undefined, NMinPotential: undefined}),
        FarmId: sLogs[0].FarmId,
        Title: `${this.translationStore.FindTranslationForSelectedLanguage('Global__New_Planning')} ${dateString}`,
        Version: i,
        UserApplicationConstant: standard,
        UserApplicationMaximum: sLogs.Reduce((aMax, log) => {
          const json = ApNFertilizationLogfilesService._getJson(log);
          const max = json['Maximum'] ? +json['Maximum'] : log.NContentMax;
          return aMax > max ? aMax : max;
        }, undefined),
        UserApplicationMinimum: sLogs.Reduce((aMin, log) => {
          const json = ApNFertilizationLogfilesService._getJson(log);
          const min = json['Minimum'] ? +json['Minimum'] : log.NContentMin;
          return aMin > min ? aMin : min;
        }, undefined),
        UserApplicationRate: applicationRate ?? 0,
        Unit: unit
      } as INFertilizationSummary;
      this._mergeLogs(sLogs).forEach((merged, n) => {
        merged.logs.forEach((log) => {
          const fieldId = this.fieldStore.getFieldByFieldGeomId(log.FieldGeomId)?.Id;
          if (fieldId == null) {
            return;
          }
          const json = ApNFertilizationLogfilesService._getJson(log);
          standard = json['Standard'] ? +json['Standard'] : (log.NContentMin + log.NContentMax) / 2;
          log.NPlanBook = {
            Id: undefined,
            ApplicationMode: applicationMode,
            Note: '',
            Summary: summary,
            ApplicationDate: log.DataSet.Created,
            FieldId: fieldId,
            Status: 0,
            OperationMode: operationMode,
            ECValue: log.Ec,
            FieldGeomId: log.FieldGeomId,
            Area: merged.area,
            ApplicationConst: standard,
            ProductId: this._getProductIdByNContent(json['AgentContent'] ? +json['AgentContent']?.split(' ')[0].Replace(',', '.') : null),
            ApplicationMax: json['Maximum'] ? +json['Maximum'] : log.NContentMax,
            ApplicationMin: json['Minimum'] ? +json['Minimum'] : log.NContentMin,
            ApplicationRate: (json['ReferenceRate'] ? +(json['ReferenceRate'].Split(' ')[0]) : standard),
            Threshold: json['Relative cutoff'],
            Unit: unit,
            Version: n,
            FarmId: log.FarmId,
            Element: 5,
            MainCropId: log.CropType,
            Geom: this.fieldStore.getFieldGeomById(log.FieldGeomId)?.Geom,
          } as unknown as INFertilizationPlanBook;
          updateLogs.push(log);
        });
      });
    });
    if (updateLogs.length !== 0) {
      this.nFertilizationStore.UpdateLogfiles(updateLogs);
    }
  }

  public update(items: any | any[], values: any): void {
    if (!Array.isArray(items)) {
      items = [items];
    }
    const logFiles = this._getLogfiles(items)
      .filter(logs => !!logs.FieldGeomId)
      .map((log) => {
        log = ObjectFactory.Copy(log);
        if (values['WorkingWidth']) {
          log.WorkingWidth = values['WorkingWidth'];
        }
        return log;
      });
    const planningMap = this._mapPlannings(logFiles, values['Planning']['SummaryId']);
    logFiles.forEach((log) => {
      if (planningMap[log.Id.toString()]) {
        log.NPlanBook = planningMap[log.Id.toString()];
      }

      if (!log.NPlanBook) {
        return;
      }

      if (values['Planning']['ProductId'] != null) {
        log.NPlanBook.ProductId = values['Planning']['ProductId'];
      }

      if (values['Driver']) {
        log.NPlanBook.ExportDriver = values['Driver'];
      }

      if (values['Machine']) {
        log.NPlanBook.ExportMachine = values['Machine'];
      }

      if (values['Attachment']) {
        log.NPlanBook.ExportInstrument = values['Attachment'];
      }

      if (values['Note'] != null) {
        log.NPlanBook.Summary.Title = values['Note'];
      }
    });
    this.nFertilizationStore.UpdateLogfiles(logFiles.filter(log => !!log && !!log.NPlanBook));
  }

  private _getLogfiles(items: any[]): IDatasetFieldMap[] {
    const ids = items.map((item) => item['Id']);
    return this.nFertilizationStore.Listen((s) => s.logfiles).getValue()
      .filter((log) => ids.some((id) => id === log.Id.toString()))
      .map((log) => {
        log = ObjectFactory.Copy(log);
        log.NPlanBook = items.find((item) => item['Id'] === log.Id)?.NPlanBook;
        return log;
      });
  }

  private _getNPlanBookLogfiles(id: string | IGuid): IDatasetFieldMap[] {
    return this.nFertilizationStore.GetLogfilesByPlanBookId(id.toString());
  }

  private _mapPlannings(logs: IDatasetFieldMap[], summaryId: string): { [key: string]: INFertilizationPlanBook } {
    const result = {};

    if (summaryId != null) {
      const plannings = this.nFertilizationStore.GetPlanningBySummaryId(summaryId);
      logs.forEach((l) => result[l.Id.toString()] = plannings.Find((pB) => pB.FieldGeomId === l.FieldGeomId));
      return result;
    }

    return result;
  }

  private _splitLogs(logs: IDatasetFieldMap[]): IDatasetFieldMap[][] {
    const result: IDatasetFieldMap[][] = [];
    const cropKeys = logs.map(l => this.cropTypeStore.getDefaultCroptype(l.CropType).CropKey).filter((v, i, a) => a.indexOf(v) === i);
    const agentContent = logs.map(l => ApNFertilizationLogfilesService._getJson(l)['AgentContent']).filter((v, i, a) => a.indexOf(v) === i);
    const application = logs.map(l => this.cropApplicationStore.GetApplicationFromLogfile(l)).filter((v, i, a) => a.indexOf(v) === i);
    const operationModes = logs.map(l => ApNFertilizationLogfilesService._getJson(l)['OperationMode']).filter((v, i, a) => a.indexOf(v) === i);

    cropKeys.forEach((ck) => {
      agentContent.forEach((aC) => {
        application.forEach((a) => {
          operationModes.forEach((o) => {
            const item = logs.filter(l => this.cropApplicationStore.GetApplicationFromLogfile(l) === a &&
              ApNFertilizationLogfilesService._getJson(l)['AgentContent'] === aC && ck === this.cropTypeStore.getDefaultCroptype(l.CropType)?.CropKey &&
              o === ApNFertilizationLogfilesService._getJson(l)['OperationMode']);
            if (item.length !== 0) {
              result.push(item);
            }
          });
        });
      });
    });

    return result;
  }

  private _getProductIdByNContent(content: number): number {
    if (!content) {
      return 0;
    }
    let min = 100 - content;
    let productId = 0;
    this.fertilizationStore.Fertilizer.filter(f => f.ContentN > 0).forEach((f) => {
      if (!(min < Math.abs(f.ContentN - content))) {
        min = Math.abs(f.ContentN - content);
        productId = f.Id;
      }
    });
    return productId;
  }

  private _mergeLogs(sLogs: IDatasetFieldMap[]): { area: number, logs: IDatasetFieldMap[] }[] {
    const fieldMap: { [fieldId: string]: { area: number, logs: IDatasetFieldMap[] } } = {};
    for (const log of sLogs) {
      const id = log.FieldGeomId as string;
      if (fieldMap[id]) {
        fieldMap[id].logs.push(log);
        fieldMap[id].area += log.Area;
      } else {
        fieldMap[id] = {area: log.Area, logs: [log]};
      }
    }
    return Object.keys(fieldMap).map((key) => fieldMap[key]);
  }

  private _getApplicationModeId(logFile: IDatasetFieldMap): IApplicationMode | null {
    const applicationMode = this.applicationModeService.GetApplicationModeById(logFile.ApplicationMode);
    if (applicationMode) {
      return applicationMode;
    }
    const operationModeId = +ApNFertilizationLogfilesService._getJson(logFile)['OperationMode'];
    const operationMode = this.operationModeService.getOperationModeByLoggerId(operationModeId);
    if (!operationMode) {
      return null;
    }
    return this.applicationModeService.GetApplicationModeByKey(ApplicationModesEnum.Variable);
  }


}
